import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { AppEndpointKeys } from '../../utils/constants/appEndpointKeys';
import { getAuthorizerHeader } from '../../utils/helpers/httpHeaders';
import type { AccountInfo } from '@azure/msal-browser';
import { sessionStorageService } from '../../utils/helpers/sessionStorageService';
import msalInstance from '../../lib/msal/msalInstance';
import { parseJwt } from '../../utils/helpers/parseJWT';

export type Role = {
  id: number;
  name: string;
  description: string | null;
  fromProfile: boolean;
};

export type RolesResponse = {
  roleAvaliables: Role[];
  rolesAssociated: Role[];
};

type UserInformationResponse = {
  token: string;
  tipo: string;
};

type TokenAttributes = {
  accessToken: string;
  atributes: string;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  name: string;
  oId: string;
  roles: string[];
};

const setConnection = () => {
  const accessToken = sessionStorageService.get('msal.active.token');
  return axios.create({
    baseURL: import.meta.env.APP_AUTHORIZER_AUTH_URL,
    headers: {
      client: import.meta.env.APP_AUTHORIZER_API_CLIENT,
      'consumer-key': import.meta.env.APP_AUTHORIZER_CONSUMER_KEY,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const refreshToken = async (account: AccountInfo): Promise<string | null> => {
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });

    const newToken = response.idToken;
    sessionStorageService.set('msal.active.token', newToken);
    console.log('Token renovado com sucesso');

    return newToken;
  } catch (error) {
    console.error('Erro ao renovar token silenciosamente:', error);

    // Se falhar a renovação silenciosa, força login interativo
    try {
      await msalInstance.acquireTokenRedirect({
        scopes: ['User.Read'],
        account: account,
      });
      return null;
    } catch (redirectError) {
      console.error('Erro ao redirecionar para login:', redirectError);
      return null;
    }
  }
};

export const getAuthInfo = async (account: AccountInfo) => {
  try {
    const connection = setConnection();
    // Getting the userId from authorizer.

    const userinformation = await connection.post<UserInformationResponse>(
      'authorizer/userinformation',
    );

    const roles = parseJwt<TokenAttributes>(userinformation.data.token).roles;

    return roles;
  } catch (error) {
    // Verifica se é erro 401/403 (não autorizado)
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (
        axiosError.response?.status === 401 ||
        axiosError.response?.status === 403
      ) {
        console.warn('Token expirado ou inválido. Tentando renovar...');

        // Tenta renovar o token
        const newToken = await refreshToken(account);

        if (newToken) {
          // Retry da requisição com novo token
          try {
            const connection = setConnection();
            // Getting the userId from authorizer.

            const userinformation =
              await connection.post<UserInformationResponse>(
                'authorizer/userinformation',
              );

            const roles = parseJwt<TokenAttributes>(
              userinformation.data.token,
            ).roles;

            return roles;
          } catch (retryError) {
            console.error(
              'Erro ao retentar após renovação do token:',
              retryError,
            );
            return undefined;
          }
        }
      }
    }

    console.error('Erro ao buscar informações de autenticação:', error);
    return undefined;
  }
};

const getUserRoles = async () => {
  const url = `${AppEndpointKeys.AUTHORIZER}/authorizer/userinformation`;

  const requestOptions = getAuthorizerHeader();

  if (requestOptions) {
    const response = await axios.get<RolesResponse>(url, requestOptions);

    return response.data.rolesAssociated;
  }

  return null;
};

const useUserRolesFetch = (isEnabled?: boolean) =>
  useQuery({
    enabled: !!isEnabled,
    queryFn: getUserRoles,
    queryKey: ['user-roles'],
  });

export default useUserRolesFetch;
